import { DropdownOptions } from '@components/inputs/DropdownInput'
import { full_timezones } from '@libs/timezones'
import { DateTime, IANAZone } from 'luxon'
import { LEGACY_TIME_ZONE_IDS } from './legacyTimeZones'

const RFC_3339_WITH_OFFSET =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/

export type EventTimeContext = {
  timeZoneId?: string | null
  timeZone?: string | null
}

export type AmbiguousTimeChoice = {
  label: string
  value: string
}

export type WallClockConversion =
  | { status: 'valid'; value: string }
  | { status: 'invalid'; message: string }
  | {
      status: 'ambiguous'
      message: string
      choices: AmbiguousTimeChoice[]
    }

const unique = <T,>(values: T[]) => Array.from(new Set(values))

export const isValidTimeZoneId = (timeZoneId?: string | null) =>
  timeZoneId === 'UTC' ||
  (typeof timeZoneId === 'string' && IANAZone.isValidZone(timeZoneId))

export const normalizeUtcTimestamp = (value: string) => {
  if (!RFC_3339_WITH_OFFSET.test(value)) {
    throw new Error('Date/time must be RFC 3339 with an explicit UTC offset')
  }

  const parsed = DateTime.fromISO(value, { setZone: true })
  if (!parsed.isValid) throw new Error('Date/time is invalid')

  return parsed.toUTC().toISO({ suppressMilliseconds: false }) as string
}

export const resolveLegacyTimeZoneId = (legacyTimeZone?: string | null) => {
  if (!legacyTimeZone) return undefined
  const candidates = LEGACY_TIME_ZONE_IDS[legacyTimeZone]
  return candidates?.length === 1 ? candidates[0] : undefined
}

export const getLegacyFixedOffsetTimeZone = (
  legacyTimeZone?: string | null
) => {
  const offset = Number(legacyTimeZone?.split('::')[0])
  if (!legacyTimeZone?.includes('::') || !Number.isFinite(offset)) {
    return undefined
  }

  const offsetMinutes = Math.round(offset * 60)
  if (offsetMinutes < -14 * 60 || offsetMinutes > 14 * 60) return undefined
  if (offsetMinutes === 0) return 'UTC'

  const sign = offsetMinutes < 0 ? '-' : '+'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const minutes = absoluteMinutes % 60
  return `UTC${sign}${hours}${
    minutes ? `:${String(minutes).padStart(2, '0')}` : ''
  }`
}

export const getEventTimeZoneId = (event?: EventTimeContext | null) => {
  if (isValidTimeZoneId(event?.timeZoneId)) return event?.timeZoneId as string
  return resolveLegacyTimeZoneId(event?.timeZone)
}

export const getEventTimeZone = (event?: EventTimeContext | null) =>
  getEventTimeZoneId(event) ?? getLegacyFixedOffsetTimeZone(event?.timeZone)

export const getBrowserTimeZoneId = () => {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return isValidTimeZoneId(browserZone) ? browserZone : 'UTC'
}

const getSupportedTimeZoneIds = () => {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[]
  }).supportedValuesOf
  const supported = supportedValuesOf?.('timeZone') ?? []
  const legacyZones = full_timezones.flatMap(({ utc }) => utc)
  return unique(['UTC', ...supported, ...legacyZones])
    .filter(isValidTimeZoneId)
    .sort((a, b) => a.localeCompare(b))
}

const offsetLabel = (dateTime: DateTime) => {
  if (dateTime.offset === 0) return 'UTC+00:00'
  const sign = dateTime.offset < 0 ? '-' : '+'
  const absolute = Math.abs(dateTime.offset)
  const hours = Math.floor(absolute / 60)
  const minutes = absolute % 60
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`
}

const displayOffsetLabel = (dateTime: DateTime) =>
  dateTime.toFormat('ZZZZ')

export const getTimeZoneOptions = (
  eventDateTime?: string | null
): DropdownOptions[] => {
  const instant = eventDateTime
    ? DateTime.fromISO(eventDateTime, { setZone: true })
    : DateTime.utc()
  const safeInstant = instant.isValid ? instant : DateTime.utc()

  return getSupportedTimeZoneIds()
    .map((timeZoneId) => {
      const zoned = safeInstant.setZone(timeZoneId)
      return {
        label: `(${offsetLabel(zoned)}) ${timeZoneId}`,
        value: timeZoneId,
        offset: zoned.offset,
      }
    })
    .sort((a, b) => a.offset - b.offset || a.label.localeCompare(b.label))
    .map(({ label, value }) => ({ label, value }))
}

export const instantToPickerDate = (
  instant: string | null | undefined,
  timeZoneId: string
) => {
  if (!instant) return null
  const zoned = DateTime.fromISO(instant, { setZone: true }).setZone(timeZoneId)
  if (!zoned.isValid) return null

  return new Date(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second,
    zoned.millisecond
  )
}

const pickerDateParts = (date: Date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds(),
  millisecond: date.getMilliseconds(),
})

export type WallClockParts = ReturnType<typeof pickerDateParts>

export const wallClockToUtc = (
  parts: WallClockParts,
  timeZoneId: string
): WallClockConversion => {
  if (!isValidTimeZoneId(timeZoneId)) {
    return {
      status: 'invalid',
      message: 'Select a valid IANA timezone before choosing a date.',
    }
  }

  const candidate = DateTime.fromObject(parts, { zone: timeZoneId })
  const roundTripMatches =
    candidate.isValid &&
    candidate.year === parts.year &&
    candidate.month === parts.month &&
    candidate.day === parts.day &&
    candidate.hour === parts.hour &&
    candidate.minute === parts.minute &&
    candidate.second === parts.second

  if (!roundTripMatches) {
    return {
      status: 'invalid',
      message: `That local time does not exist in ${timeZoneId} because the clocks change.`,
    }
  }

  const possibleOffsets = candidate
    .getPossibleOffsets()
    .sort((first, second) => first.toMillis() - second.toMillis())
  if (possibleOffsets.length > 1) {
    return {
      status: 'ambiguous',
      message: `That local time occurs twice in ${timeZoneId}. Choose an offset.`,
      choices: possibleOffsets.map((possible) => ({
        label: `${offsetLabel(possible)} (${possible.offsetNameShort})`,
        value: possible.toUTC().toISO({ suppressMilliseconds: false }) as string,
      })),
    }
  }

  return {
    status: 'valid',
    value: candidate.toUTC().toISO({ suppressMilliseconds: false }) as string,
  }
}

export const pickerDateToUtc = (
  date: Date,
  timeZoneId: string
): WallClockConversion => wallClockToUtc(pickerDateParts(date), timeZoneId)

export const reinterpretTimestamp = (
  value: string,
  fromTimeZoneId: string,
  toTimeZoneId: string
): WallClockConversion => {
  const instant = DateTime.fromISO(value, { setZone: true })
  const wall = instant.setZone(fromTimeZoneId)
  if (!instant.isValid || !wall.isValid) {
    return { status: 'invalid', message: 'Date/time is invalid' }
  }

  return wallClockToUtc(
    {
      year: wall.year,
      month: wall.month,
      day: wall.day,
      hour: wall.hour,
      minute: wall.minute,
      second: wall.second,
      millisecond: wall.millisecond,
    },
    toTimeZoneId
  )
}

const momentToLuxonTokens: Record<string, string> = {
  YYYY: 'yyyy',
  MMMM: 'LLLL',
  MMM: 'LLL',
  MM: 'LL',
  dddd: 'cccc',
  ddd: 'ccc',
  DD: 'dd',
  D: 'd',
  HH: 'HH',
  H: 'H',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  m: 'm',
  ss: 'ss',
  A: 'a',
  a: 'a',
}

export const momentFormatToLuxon = (format: string) =>
  format.replace(
    /YYYY|MMMM|MMM|MM|dddd|ddd|DD|D|HH|H|hh|h|mm|m|ss|A|a/g,
    (token) => momentToLuxonTokens[token] ?? token
  )

export const getEventDateTime = (
  instant?: string | null,
  event?: EventTimeContext | null
) => {
  const parsed = DateTime.fromISO(instant ?? '', { setZone: true })
  if (!parsed.isValid) return parsed
  const timeZone = getEventTimeZone(event)
  return timeZone ? parsed.setZone(timeZone) : parsed
}

export const formatEventInstant = (
  instant: string | null | undefined,
  momentFormat: string,
  event?: EventTimeContext | null
) => getEventDateTime(instant, event).toFormat(momentFormatToLuxon(momentFormat))

export const getEventDateTimeDisplay = (
  instant: string,
  momentFormat: string,
  event?: EventTimeContext | null,
  viewerTimeZoneId = getBrowserTimeZoneId()
) => {
  const parsed = DateTime.fromISO(instant, { setZone: true })
  const eventTimeZoneId = getEventTimeZoneId(event)
  const eventTimeZone = getEventTimeZone(event)
  const eventDateTime = eventTimeZone
    ? parsed.setZone(eventTimeZone)
    : parsed
  const viewerDateTime = parsed.setZone(viewerTimeZoneId)
  const luxonFormat = momentFormatToLuxon(momentFormat)
  const eventLocal = eventDateTime.toFormat(luxonFormat)
  const viewerLocal = viewerDateTime.toFormat(luxonFormat)

  return {
    eventLocal,
    eventZone: eventDateTime.isValid
      ? displayOffsetLabel(eventDateTime)
      : eventTimeZoneId ?? event?.timeZone ?? 'UTC',
    viewerLocal,
    viewerZone: viewerTimeZoneId,
    showEventTime:
      eventDateTime.isValid &&
      viewerDateTime.isValid &&
      eventDateTime.offset !== viewerDateTime.offset,
    dateTime: parsed.isValid
      ? (parsed.toUTC().toISO({ suppressMilliseconds: false }) as string)
      : instant,
  }
}
